import { FC, useEffect, useState } from 'react'
import { Box, Stack } from '@mui/system'
import { ActivityType } from '../../EngagementDetailsEnums'
import {
  AutoClearCooldownFromFeedOutdoorActivityType,
  AutoClearCooldownFromInvestigateOutdoorActivityType,
  CooldownManuallyClearedOutdoorActivityResultType,
  MonitoringSpecialistAssignmentOutdoorActivityType,
  OutdoorEngagement,
  ProcessedActivity,
  SystemActorType
} from '../../../../../api/models'
import { ActivityProgressBar } from '../ActivityProgressBar'
import { getMonitoringSpecialistActor, getSpecialistNames } from '../../../../../utils/specialistUtils'
import { useSpecialist } from '../../../../../api/apiQueries'
import { EscalateToIntrusionActivity } from './activity-types/EscalateToIntrusionActivity'
import { FamiliarFaceReviewWithoutAiActivity } from './activity-types/FamiliarFaceReviewWithoutAiActivity'
import { FinalDocumentationActivity } from './activity-types/FinalDocumentationActivity'
import { SharedActivity } from './activity-types/SharedActivity'
import { TwoWayAudioInteractionActivity } from './activity-types/TwoWayAudioInteractionActivity'
import { ActivityTimingResult, useActivityTiming } from './useActivityTiming'
import { MonitoringSpecialistTriageActivity } from './activity-types/MonitoringSpecialistTriageActivity'
import { SystemOrAutoClearActivityHeader } from './SystemOrAutoClearActivityHeader'
import { UserGeneratedActivityHeader } from './UserGeneratedActivityHeader'

// Narrows the `Activity` union to the specific member whose `type` field matches `K`.
// For example, ActivityByType<ActivityType.FinalDocumentationOutdoorActivityType> resolves
// to just FinalDocumentationActivity instead of the full union.
type ActivityByType<K extends ActivityType> = Extract<ProcessedActivity, { type: K }>

export interface ActivityContentProps<T extends ProcessedActivity = ProcessedActivity> {
  activity: T
  engagement: OutdoorEngagement
  timing: ActivityTimingResult
}

// The component type for rendering activity type `K`.
// Ensures each component in contentMap only accepts the specific activity type it handles.
// For example, ContentFor<ActivityType.FinalDocumentationOutdoorActivityType> resolves
// to FC<ActivityContentProps<FinalDocumentationActivity>>.
type ContentFor<K extends ActivityType> = FC<ActivityContentProps<ActivityByType<K>>>

// Maps each ActivityType to its corresponding content component.
// Each entry is optional since not every ActivityType has a dedicated component.
type ActivityTypeToComponentMap = {
  [K in ActivityType]?: ContentFor<K>
}

const activityComponentMap: ActivityTypeToComponentMap = {
  [ActivityType.RecentHistoryOutdoorActivityType]: SharedActivity,
  [ActivityType.TwoWayAudioCheckpointOutdoorActivityType]: SharedActivity,
  [ActivityType.QuickClearFromInvestigateOutdoorActivityType]: SharedActivity,
  [ActivityType.FamiliarFaceReviewWithoutAIOutdoorActivityType]: FamiliarFaceReviewWithoutAiActivity,
  [ActivityType.TwoWayAudioInteractionOutdoorActivityType]: TwoWayAudioInteractionActivity,
  [ActivityType.FinalDocumentationOutdoorActivityType]: FinalDocumentationActivity,
  [ActivityType.MonitoringSpecialistTriageOutdoorActivityType]: MonitoringSpecialistTriageActivity,
  [ActivityType.EscalateToIntrusionWithDescriptionOutdoorActivityType]: EscalateToIntrusionActivity
}

function isAutoClearType(type: string): boolean {
  return (
    type === ActivityType.AutoClearCooldownFromFeedOutdoorActivityType || type === ActivityType.AutoClearCooldownFromInvestigateOutdoorActivityType
  )
}

function mapToActivityComponent(activity: ProcessedActivity): FC<ActivityContentProps> | null {
  const manuallyCleared = activity.completed?.result?.type === CooldownManuallyClearedOutdoorActivityResultType.CooldownManuallyCleared
  if (isAutoClearType(activity.type) && manuallyCleared) {
    return SharedActivity
  }
  return (activityComponentMap[activity.type] as FC<ActivityContentProps> | undefined) ?? null
}

export interface ActivityCardProps {
  engagement: OutdoorEngagement
  processedActivity: ProcessedActivity
  currentTimeInSeconds?: number
  isPlaying?: boolean
  shouldExpandAll?: boolean
  expandCollapseVersion?: number
  cardIndex?: number
  activityTypeIndex?: number
  activityTypeTotal?: number
  onCollapsedChange?: (index: number, isCollapsed: boolean) => void
  onSeek?: (timeInSeconds: number) => void
}

export const ActivityCard: FC<ActivityCardProps> = ({
  engagement,
  processedActivity,
  currentTimeInSeconds = 0,
  isPlaying = false,
  shouldExpandAll = false,
  expandCollapseVersion = 0,
  cardIndex = 0,
  activityTypeIndex = 0,
  activityTypeTotal = 0,
  onCollapsedChange,
  onSeek
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true)
  const timing = useActivityTiming(processedActivity, currentTimeInSeconds, isPlaying)

  const isSystemGeneratedActivity =
    !!processedActivity.assigned?.assignedTo &&
    'type' in processedActivity.assigned.assignedTo &&
    processedActivity.assigned.assignedTo.type === SystemActorType.System

  const isAutoClearedManually = processedActivity.completed?.result?.type === CooldownManuallyClearedOutdoorActivityResultType.CooldownManuallyCleared

  const isSystemOrAutoClearActivity =
    isSystemGeneratedActivity ||
    (processedActivity.type === AutoClearCooldownFromFeedOutdoorActivityType.AutoClearCooldownFromFeed && !isAutoClearedManually) ||
    (processedActivity.type === AutoClearCooldownFromInvestigateOutdoorActivityType.AutoClearCooldownFromInvestigate && !isAutoClearedManually)

  const specialistDetails =
    processedActivity.type === MonitoringSpecialistAssignmentOutdoorActivityType.MonitoringSpecialistAssignment
      ? getMonitoringSpecialistActor(processedActivity)
      : undefined

  const specialistQuery = useSpecialist(specialistDetails?.monitoringServiceProviderId, specialistDetails?.specialistId, !!specialistDetails)
  const assignedSpecialist = getSpecialistNames([specialistQuery.data])

  const isPlayingChapter = isPlaying && timing.isActive
  const MappedActivityComponent = mapToActivityComponent(processedActivity)

  useEffect(() => {
    if (expandCollapseVersion > 0) {
      setIsCollapsed(!shouldExpandAll)
    }
  }, [expandCollapseVersion, shouldExpandAll])

  useEffect(() => {
    onCollapsedChange?.(cardIndex, isCollapsed)
  }, [cardIndex, isCollapsed, onCollapsedChange])

  const toggleCollapsedState = () => {
    setIsCollapsed(!isCollapsed)
  }

  return (
    <Stack
      direction="column"
      sx={{
        borderBottom: '1px solid var(--border-medium)',
        backgroundColor: isPlayingChapter ? 'var(--neutral-500-t40)' : 'inherit'
      }}
      data-testid={`activity-card-type-${processedActivity.type}`}
      data-active={isPlayingChapter}
    >
      <Stack direction="column" sx={{ padding: 'var(--spacing-s)', gap: 'var(--spacing-2xs)' }}>
        {isSystemOrAutoClearActivity && (
          <SystemOrAutoClearActivityHeader
            activityType={processedActivity.type}
            isPlayingChapter={isPlayingChapter}
            durationMs={timing.durationMs}
            assignedSpecialist={assignedSpecialist}
          />
        )}
        {!isSystemOrAutoClearActivity && (
          <UserGeneratedActivityHeader
            processedActivity={processedActivity}
            isCollapsed={isCollapsed}
            timing={timing}
            activityTypeIndexOf={activityTypeTotal > 1 ? `${activityTypeIndex} of ${activityTypeTotal}` : undefined}
            onSeek={onSeek}
            onToggleCollapsed={toggleCollapsedState}
          />
        )}
        {timing.isActive && !isSystemOrAutoClearActivity && (
          <Box
            sx={{ marginTop: isCollapsed ? 'var(--spacing-2xs)' : 'var(--spacing-none)' }}
            data-testid={`activity-card-progress-bar-${processedActivity.type}`}
          >
            <ActivityProgressBar currentTimeInSeconds={timing.elapsedSeconds} totalTimeInSeconds={timing.durationSeconds} />
          </Box>
        )}
        {!isCollapsed && MappedActivityComponent && (
          <Box data-testid={`activity-card-expanded-${processedActivity.type}`}>
            <MappedActivityComponent activity={processedActivity} engagement={engagement} timing={timing} />
          </Box>
        )}
      </Stack>
    </Stack>
  )
}
